"use strict";
import { Percent } from "@uniswap/sdk-core";
import Expand from "components/Expand";
import QuestionHelper from "components/QuestionHelper";
import Row, { RowBetween } from "components/Row";
import { Input, InputContainer } from "components/Settings/Input";
import styled from "lib/styled-components";
import { useState, useEffect } from "react";
import { CautionTriangle, ThemedText } from "theme/components";
import { Text } from "ui/src";
import { Trans } from "uniswap/src/i18n";
import { useFormatter } from "utils/formatNumbers";
var SlippageError = /* @__PURE__ */ ((SlippageError2) => {
  SlippageError2["InvalidInput"] = "InvalidInput";
  return SlippageError2;
})(SlippageError || {});
const Option = styled(Row)`
  width: auto;
  cursor: pointer;
  padding: 6px 12px;
  text-align: center;
  gap: 4px;
  border-radius: 12px;
  background: ${({ isActive, theme }) => isActive ? theme.darkMode ? theme.accent2 : theme.surface3 : "transparent"};
  pointer-events: ${({ isActive }) => isActive && "none"};
`;
const Switch = styled(Row)`
  width: auto;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.surface3};
  border-radius: 16px;
`;
const NUMBER_WITH_MAX_FOUR_DECIMAL_PLACES = /^(?:\d*\.\d{0,4}|\d+)$/;
const MIN_SLIPPAGE = 1e-4;
const MAX_SLIPPAGE = 1;
const MINIMUM_RECOMMENDED_SLIPPAGE = 5e-4;
const MAXIMUM_RECOMMENDED_SLIPPAGE = 0.1;
function useFormatPercentInput() {
  const { formatPercent } = useFormatter();
  return (slippage) => formatPercent(slippage).slice(0, -1);
}
function decimalToPercent(decimal) {
  const percentage = decimal * 100;
  return new Percent(Math.floor(percentage * 100), 1e4);
}
export function AggregatorSlippageSettings({
  slippage,
  onSlippageChange,
  autoSlippage = 5e-3
}) {
  const { formatPercent } = useFormatter();
  const formatPercentInput = useFormatPercentInput();
  const slippagePercent = decimalToPercent(slippage);
  const autoSlippagePercent = decimalToPercent(autoSlippage);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const isAuto = !isCustomMode && Math.abs(slippage - autoSlippage) < 1e-4;
  const formatSlippageInput = (value) => {
    const percent = decimalToPercent(value);
    return formatPercentInput(percent);
  };
  const defaultSlippageInputValue = isAuto ? "" : formatSlippageInput(slippage);
  const [slippageInput, setSlippageInput] = useState(defaultSlippageInputValue);
  const [slippageError, setSlippageError] = useState(false);
  const [isOpen, setIsOpen] = useState(!isAuto);
  useEffect(() => {
    if (isAuto) {
      setSlippageInput("");
    } else {
      setSlippageInput(formatSlippageInput(slippage));
    }
  }, [slippage, isAuto]);
  const parseSlippageInput = (value) => {
    if (value.length > 0 && !NUMBER_WITH_MAX_FOUR_DECIMAL_PLACES.test(value)) {
      return;
    }
    setSlippageInput(value);
    setSlippageError(false);
    if (value.length === 0) {
      onSlippageChange(autoSlippage);
      return;
    }
    if (value === ".") {
      return;
    }
    try {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) {
        setSlippageError("InvalidInput" /* InvalidInput */);
        return;
      }
      const decimalValue = parsed / 100;
      if (decimalValue < MIN_SLIPPAGE) {
        onSlippageChange(MIN_SLIPPAGE);
        setSlippageError("InvalidInput" /* InvalidInput */);
      } else if (decimalValue > MAX_SLIPPAGE) {
        onSlippageChange(MAX_SLIPPAGE);
        setSlippageError("InvalidInput" /* InvalidInput */);
      } else {
        onSlippageChange(decimalValue);
      }
    } catch (e) {
      setSlippageError("InvalidInput" /* InvalidInput */);
    }
  };
  const tooLow = slippage < MINIMUM_RECOMMENDED_SLIPPAGE && slippage >= MIN_SLIPPAGE;
  const tooHigh = slippage > MAXIMUM_RECOMMENDED_SLIPPAGE && slippage <= MAX_SLIPPAGE;
  return <Expand
    testId="aggregator-max-slippage-settings"
    padding="6px 0px"
    isOpen={isOpen}
    onToggle={() => setIsOpen(!isOpen)}
    header={<Row width="auto">
      <ThemedText.BodyPrimary><Trans i18nKey="settings.maxSlippage" /></ThemedText.BodyPrimary>
      <QuestionHelper text={<Trans i18nKey="swap.settings.transactionRevertPrice" />} />
    </Row>}
    button={<ThemedText.BodyPrimary>{isAuto ? <Trans i18nKey="common.automatic" /> : formatPercent(slippagePercent)}</ThemedText.BodyPrimary>}
  >
    <RowBetween gap="md">
      <Switch>
        <Option
          onClick={() => {
            setIsCustomMode(false);
            setSlippageInput("");
            onSlippageChange(autoSlippage);
          }}
          isActive={isAuto}
        ><ThemedText.BodyPrimary><Trans i18nKey="common.automatic" /></ThemedText.BodyPrimary></Option>
        <Option
          onClick={() => {
            setIsCustomMode(true);
            const valueToUse = isAuto ? autoSlippage : slippage;
            onSlippageChange(valueToUse);
            setSlippageInput(formatSlippageInput(valueToUse));
          }}
          isActive={!isAuto}
        ><ThemedText.BodyPrimary><Trans i18nKey="common.custom" /></ThemedText.BodyPrimary></Option>
      </Switch>
      <InputContainer gap="md" error={!!slippageError}>
        <Input
          data-testid="aggregator-slippage-input"
          placeholder={formatPercentInput(autoSlippagePercent)}
          value={slippageInput}
          onChange={(e) => parseSlippageInput(e.target.value)}
          onBlur={() => {
            if (isAuto) {
              setSlippageInput("");
            } else {
              setSlippageInput(formatSlippageInput(slippage));
            }
            setSlippageError(false);
          }}
        />
        <Text variant="body1" color={slippageError ? "$statusCritical" : "$neutral1"}>%</Text>
      </InputContainer>
    </RowBetween>
    {tooLow || tooHigh ? <RowBetween gap="md">
      <CautionTriangle />
      <ThemedText.BodySmall color="deprecated_accentWarning">{tooLow ? <Trans
        i18nKey="swap.slippageBelow.warning"
        values={{ amt: formatPercent(decimalToPercent(MINIMUM_RECOMMENDED_SLIPPAGE)) }}
      /> : <Trans i18nKey="swap.frontrun.warning" />}</ThemedText.BodySmall>
    </RowBetween> : null}
  </Expand>;
}
